<template>
    <div v-if="loading" class="flex flex-column justify-content-center align-items-center">
        <h4 class="text-center">Loading...</h4>
        <ScaleLoader class="m-auto" />
    </div>
    <div v-if="!loading" class="card">
        <div class="grid">
            <div class="col-12 flex justify-content-center align-items-center">
                <h5 class="m-2">General Information:</h5>
            </div>
            <div class="form-field col-3">
                <label for="name" class="font-semibold">Metric Name</label>
                <InputText maxlength = "255" v-model="metric.name" id="name" placeholder="Enter metric name" />
            </div>
            <div class="form-field col-3">
                <label for="code" class="font-semibold">Code</label>
                <InputText maxlength = "255" v-model="metric.code" id="code" placeholder="Enter metric code" />
            </div>
            <div class="form-field col-3">
                <label for="type" class="font-semibold">Type</label>
                <Dropdown id="type" v-model="metric.type" :options="getTypes()" placeholder="Select a metric type" optionLabel="name" optionValue="value" />
            </div>
            <div class="form-field col-3">
                <label for="sharable" class="font-semibold">Sharable</label>
                <Dropdown :disabled="metric.series.length > 0" id="sharable" v-model="metric.sharable" :options="getSharable()" placeholder="Select sharable" optionLabel="name" optionValue="value" />
            </div>
            <div class="form-field col-12">
                <label for="description" class="font-semibold">Description</label>
                <InputText maxlength = "255" v-model="metric.description" id="description" placeholder="Enter metric description" />
            </div>
        </div>
    </div>

    <div v-if="!loading">
        <div class="grid">

            <div class="col-6">
                <div class="card">
                    <div class="grid">
                        <div v-if="!addDetailsEnabled" class="col-12 flex">
                            Please, fill the Metric name, the Code and the Type before adding a series.
                        </div>

                        <div class="col-4 flex justify-content-center align-items-center">
                            <h5 class="m-2">Series:</h5>
                        </div>
                        <div class="col-8 flex justify-content-end align-items-center">
                            <Dropdown
                                v-if="props.id"
                                id="metric-edit-date-range"
                                v-model="selectedMonths"
                                :options="dateRangeOptions"
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Period"
                                class="metric-period-dropdown mr-2"
                                v-tooltip.top="'Assessment date range for this metric'"
                                @change="onPeriodChange"
                            />
                            <Button :disabled="saving || !saveMetricEnabled" label="Save and Update" icon="pi pi-save" class="ml-2 p-button-primary mr-2" @click="onSaveAndUpdate" />
                            <Button :disabled="!addDetailsEnabled || sortedGroups.length > 1" label="Add" icon="pi pi-plus" class="p-button-info" @click="onAddSeries" />
                        </div>

                        <div class="col-12">
                            Every Series could be linked with a totally different Datasource.
                            All the series will share the same groups and will be displayed together.
                            For this reason, it's suggested to chose the same Datasource or compatible Datasources.
                        </div>

                        <div class="p-menu col-12 flex">
                        <TabView v-model:activeIndex="activeSeriesTab" style="width: 100%;">
                            <TabPanel header="Individual Series">
                                <div class="flex grid" v-if="activeSeriesTab === 0">

                                    <template v-for="series in metric.series" :key="series.id">
                                        <div class="col-12 flex-nowrap align-content-end flex justify-content-end mb-0">
                                            <div class="flex justify-content-end flex-wrap">
                                                <Button icon="pi pi-pencil" class="p-button-rounded p-button-success mr-2"
                                                        v-tooltip="'Edit series'" @click="editSeries(series)" />
                                                <Button icon="pi pi-trash" class="p-button-rounded p-button-danger mr-2"
                                                        v-tooltip="'Delete series'" @click="deleteSeries(series.id)" />
                                            </div>
                                        </div>
                                        <div class="col-12 mt-0">
                                            <MetricGraph :type="metric.type" :datasets="series.dataset" :table-data="series.tableData" :metricData="metricSeriesGraphData" :selectedSeriesId="series.id"/>
                                        </div>
                                    </template>
                                </div>
                            </TabPanel>
                            <TabPanel header="Overall Graph"  :disabled="metric.series.length <= 1" >
                                <div class="flex grid" v-if="activeSeriesTab === 1" style="width: 100%;">
                                    <div class="col-12">
                                    <!--
                                    type: {{metric.type}}<br />
                                    labels: {{metric.series.length}}<br />
                                    result: {{metric.series}}<br /><br />
                                    -->

                                        <MetricGraph :type="metric.type" :datasets="graphDatasets" :table-data="tableData"  :metricData="metricSeriesGraphData"/>


                                    </div>
                                </div>
                            </TabPanel>
                        </TabView>
                        </div>


                    </div>
                </div>
            </div>

            <div class="col-6">
                <div class="card">

                    <div class="grid">

                        <div v-if="!saveMetricEnabled" class="col-12 flex ">
                            Please add at least a Series
                        </div>

                        <div class="col-7 flex justify-content-center align-items-center">
                            <h5 class="m-2">Groups:</h5>
                        </div>
                        <div class="col-5 flex justify-content-end">
                            <Button :disabled="saving || !saveMetricEnabled" label="Save and Update" icon="pi pi-save" class="ml-2 p-button-primary mr-2" @click="onSaveAndUpdate" />
                            <Button :disabled="
                                !saveMetricEnabled ||
                                sortedGroups.length >= 2 ||
                                (metric.type === 'pie' && sortedGroups.length >= 1) ||
                                (metric.type === 'line' && sortedGroups.length >= 1) ||
                                (metric.series.length > 1  && sortedGroups.length >= 1)
                            " label="Add" icon="pi pi-plus" class="p-button-info" @click="onAddGroup" />
                        </div>

                        <div class="col-12">
                            If no groups are added, the metric is only capable to display a counter.
                            Some graph types are restricted to only one group while `bars` are restricted to two  for readability.
                        </div>

                        <template v-for="(group, index) in sortedGroups" :key="group.id">
                            <div class="col-4">
                                <template v-if="group.procedure === 'sex'">
                                    Sex
                                </template>
                                <template v-else-if="group.procedure === 'age_today_range'">
                                    Age today range
                                </template>
                                <template v-else-if="group.procedure === 'age_assessment_range'">
                                    Assessment date age range
                                </template>
                                <template v-else-if="group.procedure === 'pass_fail'">
                                    Pass / fail
                                </template>
                                <template v-else-if="group.procedure === 'users'">
                                    Users
                                </template>
                                <template v-else-if="group.procedure === 'values'">
                                    Values
                                </template>
                                <template v-else-if="group.procedure === 'companies'">
                                    Companies
                                </template>
                                <template v-else>{{ group.procedure }}</template>

                            </div>
                            <div class="col-2">
                                <template v-if="group.procedure === 'values'">
                                    {{ valueGroupConfig }}
                                </template>
                                <template v-else-if="group.procedure !== 'users'">
                                    {{ group.first }}
                                </template>
                                <template v-else>
                                    {{userGroupUserListString}}
                                </template>

                            </div>
                            <div class="col-3">
                                {{ group.second }}
                            </div>

                            <div class="col-3 flex justify-content-end">
                                <Button v-if="index > 0 && sortedGroups.length > 1" icon="pi pi-sort-up" class="p-button-rounded p-button-outlined p-button-success mr-2"
                                        v-tooltip="'Move up'" @click="onMoveUp(index)" />
                                <Button v-if="sortedGroups.length > 1 && (index+1) < sortedGroups.length" icon="pi pi-sort-down" class="p-button-rounded p-button-outlined p-button-success mr-2"
                                        v-tooltip="'Move down'" @click="onMoveDown(index)" />
                                <Button icon="pi pi-pencil" class="p-button-rounded p-button-success mr-2"
                                        v-tooltip="'Edit group'" @click="editGroup(group)" />
                                <Button icon="pi pi-trash" class="p-button-rounded p-button-danger mr-2"
                                        v-tooltip="'Delete group'" @click="deleteGroup(group.id)" />
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div v-if="!loading" class="card">
        <div class="grid">

            <div class="col-12">
                <div class="flex justify-content-end">
                    <Button label="Cancel" icon="pi pi-times" class="p-button-danger" @click="onCancel" />
                    <Button :disabled="saving || !saveMetricEnabled" label="Save and Update" icon="pi pi-save" class="ml-2 p-button-primary" @click="onSaveAndUpdate" />
                    <Button :disabled="saving || !saveMetricEnabled" label="Submit" icon="pi pi-check" class="ml-2 p-button-primary" @click="onSubmit" />
                </div>
            </div>
        </div>
    </div>

    <CreateUpdateMetricSeries :display="editSeriesDialogVisible"
                              @hideMetricSeriesPopup="hideMetricSeriesPopup"
                              @saveMetricSeriesData="onSaveMetricSeriesData"
                              :sharable="metric.sharable"
                              :series-data="series"
                              :assessments="assessmentsList"
                              :attributes="attributesList"
                              :tags="tagsList"
    />

    <CreateUpdateMetricGroup :display="editGroupDialogVisible"
                              @hideMetricGroupPopup="hideMetricGroupPopup"
                              @saveMetricGroupData="onSaveMetricGroupData"
                              :sharable="metric.sharable"
                              :group-data="group"
                              :attributes="attributesCheckBoxesList"
                              :tags="tagsListForGroup"
    />
</template>

<script setup>

import { computed, defineProps, onBeforeMount, onMounted, ref } from 'vue';
import store from '@/store';
import GlobalRequest from '@/api/global-request';
import { useRouter } from 'vue-router';
import CreateUpdateMetricSeries from '@/components/metrics/components/CreateUpdateMetricSeries.vue';
import CreateUpdateMetricGroup from '@/components/metrics/components/CreateUpdateMetricGroup.vue';
// import MetricGraphOld from '@/components/metrics/components/MetricGraphOld.vue';
import MetricGraph from '@/components/metrics/components/MetricGraph.vue';
import { DateTime } from 'luxon';
import { showToast } from '@/helpers/toast-handler';

const router = useRouter();

const props = defineProps({
    id: {
        type: [String, Number],
        required: false,
    }
});

const company_id = computed(() => {
    return store.getters['auth/userCompanyId']?.id;
});

const  metric = ref({
    id: null,
    company_id: null,
    name: null,
    code: null,
    description: null,
    type: null,
    sharable: 0,
    series: [],
    groups: [],
})

const emptySeries = () => {
    return {
        id: null,
        label: null,
        datasource_id: null,
        red: null,
        green: null,
        blue: null,
        operation: null,
        assessment_code: null,
        attribute_code: null,
        tag_id: null,
    };
}

const emptyGroup = () => {
    return {
        id: null,
        procedure: null,
        first: null,
        second: null,
        order: 0,
    }
}

const series = ref(emptySeries())
const group = ref(emptyGroup())

const loading = ref(true);

const editSeriesDialogVisible = ref(false);
const editGroupDialogVisible = ref(false);

const saving = ref(false);

const activeSeriesTab = ref(0);

const userGroupUserList = ref([])

const userGroupUserListString = computed(()=>{
    //console.log('userGroupUserList', userGroupUserList)
    return userGroupUserList.value.join(' / ');
})

const tableData = ref([])

const graphDatasets = ref({})

const valueGroupConfig = ref('')

const assessmentsList = ref([]);

const attributesList = ref([]);
const attributesCheckBoxesList = ref([]);

const tagsList = ref([]);
const tagsListForGroup = ref([]);

const metricSeriesGraphData = ref({})

const dateRangeOptions = [
    { label: 'Last 3 months', value: 3 },
    { label: 'Last 6 months', value: 6 },
    { label: 'Last 12 months', value: 12 },
    { label: 'All time', value: 0 },
]

// Default Last 3 months on edit (same as dashboard); 0 = all time (no months param)
const selectedMonths = ref(3)

const sortedGroups = computed(() => {

    metric.value.groups.forEach(item => {

        //console.log('item.first', item.first);

        if (item.first!== null && item.procedure === 'values' && item.first.length > 0) {
            //console.log('TODO values first', item.first)
        } else if (item.first!== null && item.procedure === 'users' && item.first.length > 0) {
            userGroupUserList.value = [];

            const users = JSON.parse(item.first)

            if (typeof users !== 'undefined') {

                for (const user of users) {

                    GlobalRequest.getRequest('v2/admin/users/' + user, {
                        params: {
                            company_id: company_id.value,
                        },
                    }).then((response) => {
                        if (response.statusCode === 200) {
                            userGroupUserList.value.push(response.data.name + ' ' + response.data.surname)

                            //item.first = userList.join(' / ');

                        }
                    });
                }
            }
        }
    })

    let sorted = metric.value.groups;

    if (sorted !== null) {
        return sorted.sort((a, b) => {
            return a.order - b.order;
        })
    } else {
        return null;
    }
});

function parseValueGroupConfig(valueSetup) {

    if (Object.hasOwn(valueSetup, 'attribute_code')) {
        //console.log("@@@@ ",valueSetup.attribute_code)

        GlobalRequest.getRequest('v2/template/attributes', {
            params: {
                company_id: company_id.value,
                code_exact: valueSetup.attribute_code,
                //used: true,
                default: true,
                paginate: false,
                user_profile: 'both',
            },
        }).then((response) => {
            if (response.statusCode === 200) {
                //console.log('response', response.data)

                // The request should result in a list of a single element.

                if (response.data.length >= 1) {
                    valueGroupConfig.value = response.data[0].label
                }
            } else {
                valueGroupConfig.value = ''
            }
        });
    } else if (Object.hasOwn(valueSetup, 'tag_id')) {
        //console.log("@@@@ ",valueSetup.tag_id)
        // http://localhost:8888/api/v2/template/attributes/tags/3

        GlobalRequest.getRequest('v2/template/attributes/tags/'+valueSetup.tag_id).then((response) => {
            if (response.statusCode === 200) {
                if (response.data.description.length > 0) {
                    valueGroupConfig.value = response.data.description
                } else if (response.data.attribute && response.data.attribute.label.length > 0) {
                    valueGroupConfig.value = response.data.attribute.label
                } else {
                    valueGroupConfig.value = response.data.code
                }

            } else {
                valueGroupConfig.value = ''
            }
        });

        //valueGroupConfig.value = 'tag id: '+valueSetup.tag_id
    }
}

function onMoveUp(index) {
    const prevIndex = index-1;

    if (index in sortedGroups.value && prevIndex in sortedGroups.value) {
        const currentOrder = sortedGroups.value[index].order;
        const prevOrder = sortedGroups.value[prevIndex].order;

        sortedGroups.value[prevIndex].order = currentOrder;
        sortedGroups.value[index].order = prevOrder;
    }
}

function onMoveDown(index) {
    const nextIndex = index+1;

    if (index in sortedGroups.value && nextIndex in sortedGroups.value) {
        const currentOrder = sortedGroups.value[index].order;
        const nextOrder = sortedGroups.value[nextIndex].order;

        sortedGroups.value[nextIndex].order = currentOrder;
        sortedGroups.value[index].order = nextOrder;
    }
}

function onAddSeries() {
    series.value = emptySeries();

    editSeriesDialogVisible.value = true;
}

function onAddGroup() {
    group.value = emptyGroup();

    let order = 0;

    metric.value.groups.forEach((item) => {
        if (item.order > order) {
            order = item.order;
        }
    })

    group.value.order = order+1;

    editGroupDialogVisible.value = true;
}

function hideMetricSeriesPopup() {
    editSeriesDialogVisible.value = false;
}

function hideMetricGroupPopup() {
    editGroupDialogVisible.value = false;
}

onMounted(() => {
    GlobalRequest.getRequest('v2/template/attributes', {
        params: {
            company_id: company_id.value,
            default: true,
            used: true,
            paginate: false,
            user_profile: 'both',
        },
    }).then((response) => {
        if (response.statusCode === 200) {
            response.data.forEach((item) => {
                let label = item.label;
                if (label && label.length >= 60) {
                    label = item.label.substring(0, 57)+'...';
                }

                const type = parseInt(item.type)

                if (type === 10 || type === 8 || type === 9 || type === 11) {
                    attributesCheckBoxesList.value.push({
                        code: item.code,
                        name: label,
                    })
                }

                attributesList.value.push({
                    code: item.code,
                    name: label,
                })
            })
        }
    });

    GlobalRequest.getRequest('v2/template/assessment', {
        params: {
            company_id: company_id.value,
            default: true,
        },
    }).then((response) => {
        if (response.statusCode === 200) {
            response.data.forEach((item) => {
                assessmentsList.value.push({
                    code: item.code,
                    name: item.name,
                })
            })
        }
    });

    // /v2/template/attributes/tags

    GlobalRequest.getRequest('v2/template/attributes/tags', {
        params: {
            company_id: company_id.value,
        },
    })
        .then((response) => {
        if (response.statusCode === 200) {

            response.data.forEach((item) => {
                let description = item.code;

                //console.log('TAG', item)

                const permittedTypes = [8, 9, 10, 11];

                if (item.description.length > 0) {
                    description += ' | ' + item.description;
                }

                if (description.length >= 60) {
                    description = description.substring(0, 57)+'...';
                }

                tagsList.value.push({
                    id: item.id,
                    description: description,
                })

                if (item.attribute && permittedTypes.includes(parseInt(item.attribute.type))) {
                    tagsListForGroup.value.push({
                        id: item.id,
                        description: description,
                    })

                }
            })
        }
    });
})



function onSaveMetricGroupData(data) {

    if (data.id !== null && data.id > 0) {
        metric.value.groups.forEach( (group, index) => {

            if (group.id === data.id) {
                metric.value.groups[index] = {
                    id: data.id,
                    first: data.first,
                    second: data.second,
                    order: data.order,
                    procedure: data.procedure,
                }
            }
        })
    } else {
        metric.value.groups.push({
            id: data.id,
            first: data.first,
            second: data.second,
            order: data.order,
            procedure: data.procedure,
        });
    }

    saveMetric(metric);
}

function saveMetric(metric, callback) {
    saving.value = true;
    if (metric.value.id !== null && metric.value.id > 0) {

        GlobalRequest.patchRequest('/v2/datasource/metric/'+metric.value.id , metric.value)
            .then((response) => {
                if (response.statusCode === 200) {
                    parsingMetricResponse(response)

                    metricSeriesGraphData.value = response.data
                }

                saving.value = false;

                if (typeof callback !== 'undefined') {
                    callback();
                }
            }).catch((response) => {
            showToast(response, response.data.message);
            console.error('something went wrong:', response, metric.value.series);

            saving.value = false;
            router.go(-1)
        })
    } else {
        GlobalRequest.postRequest('/v2/datasource/metric' , metric.value)
            .then((response) => {
                if (response.statusCode === 201) {
                    parsingMetricResponse(response)
                    metricSeriesGraphData.value = response.data
                }

                saving.value = false;

                if (typeof callback !== 'undefined') {
                    callback();
                }
            }).catch((response) => {
            console.error('something went wrong:', response, metric.value.series);
            showToast(response, response.data.message);

            saving.value = false;
            router.go(-1)
        })
    }
}

function onSaveMetricSeriesData(data) {

    if (data.id !== null && data.id > 0) {
        // update

        metric.value.series.forEach( (series, index) => {

            if (series.id === data.id) {
                metric.value.series[index] = {
                    id: data.id,
                    label: data.label,
                    datasource_id: data.datasource_id,
                    red: data.red,
                    green: data.green,
                    blue: data.blue,
                    operation: data.operation,
                    assessment_code: data.assessment_code,
                    attribute_code: data.attribute_code,
                    tag_id: data.tag_id,
                }
            }
        })
    } else {
        // insert
        metric.value.series.push(
            {
                label: data.label,
                datasource_id: data.datasource_id,
                red: data.red,
                green: data.green,
                blue: data.blue,
                operation: data.operation,
                assessment_code: data.assessment_code,
                attribute_code: data.attribute_code,
                tag_id: data.tag_id,
            }
        );
    }

    saveMetric(metric);
}

function onCancel() {
    router.go(-1)
}

function onSaveAndUpdate() {
    saveMetric(metric, () => {});
}

function onSubmit() {
    saveMetric(metric, () => {router.go(-1)})
}

function editGroup(groupData) {

    editGroupDialogVisible.value = true;

    group.value.id = groupData.id;
    group.value.procedure = groupData.procedure;
    group.value.first = groupData.first;
    group.value.second = groupData.second;
    group.value.order = groupData.order;
}

function editSeries(seriesData) {

    editSeriesDialogVisible.value = true;

    series.value.id = seriesData.id;
    series.value.label = seriesData.label;
    series.value.datasource_id = seriesData.datasource_id;
    series.value.red = seriesData.red;
    series.value.green = seriesData.green;
    series.value.blue = seriesData.blue;
    series.value.operation = seriesData.operation;
    series.value.assessment_code = seriesData.assessment_code;
    series.value.attribute_code = seriesData.attribute_code;
    series.value.tag_id = seriesData.tag_id;
}

function deleteSeries(seriesId) {

    let toRemove = null;

    if (seriesId !== null && seriesId > 0) {
        metric.value.series.forEach( (series, index) => {

            if (series.id === seriesId) {
                toRemove = index;
            }
        })

        metric.value.series.splice(toRemove, 1);
    }

    // saveMetric(metric);
}

function deleteGroup(groupId) {

    let toRemove = null;

    if (groupId !== null && groupId > 0) {
        metric.value.groups.forEach( (groups, index) => {

            if (groups.id === groupId) {
                toRemove = index;
            }
        })

        metric.value.groups.splice(toRemove, 1);
    }

    // At this point, it's possible than the order has a hole.

    sortedGroups.value.forEach((item, index) => {
        item.order = index + 1;
    })

    // saveMetric(metric);
}

const getTypes = () => {
    return [
        { name: 'Table', value: 'table' },
        { name: 'Bar', value: 'bar' },
        { name: 'Line', value: 'line' },
        { name: 'Pie', value: 'pie' },
    ];
};

const getSharable = () => {
    return [
        { name: 'False', value: 0 },
        { name: 'True', value: 1 },
    ];
};

onBeforeMount(async () => {
    if (props.id !== undefined) {
        loadMetricDetail(props.id);
    } else {
        metric.value.company_id = company_id.value;
        loading.value = false;
        metric.value.sharable = 0;
    }
});

/**
 * When confirming the series or the group, the metric is saved.
 * Requirements:
 * Name must be a string.
 * Code must be a string.
 * Type must be a string.
 */
const addDetailsEnabled = computed(() => {
    return (
        (metric.value.name !== null && metric.value.name.length > 0) &&
        (metric.value.code !== null && metric.value.code.length > 0) &&
        (metric.value.type !== null && metric.value.type.length > 0)
    );
});

/**
 * When confirming the series or the group, the metric is saved.
 * Requirements:
 * Name must be a string. (see addDetailsEnabled)
 * Code must be a string. (see addDetailsEnabled)
 * Type must be a string. (see addDetailsEnabled)
 * Series must be an array.
 */
const saveMetricEnabled = computed(() => {
    return addDetailsEnabled.value && metric.value.series != null && metric.value.series.length > 0;
});

function buildTableData(resultData, labels, seriesLabel) {
    let headers = []

    if (resultData.length > 0) {
        const firstTableRow = resultData[0]

        for (const prop in firstTableRow) {

            let found = false;

            if (labels.length > 0) {
                labels.forEach(header => {
                    /*
                    If there are multiple series, all of them will share all the keys based on groups.
                    Removing duplicates.
                     */

                    if (prop === header.field) {
                        found = true;
                    }
                })
            }

            if (!found) {
                if (prop === 'groupBySex') {
                    headers.push({ field: prop, header: 'Sex' });
                } else if (prop === 'groupByPassFail') {
                    headers.push({ field: prop, header: 'Pass / fail' });
                } else if (prop === 'age_today_range') {
                    headers.push({ field: prop, header: 'Today age range' });
                } else if (prop === 'age_assessment_range') {
                    headers.push({ field: prop, header: 'Assessment date age range' });
                } else if (prop === 'groupByUsers') {
                    headers.push({ field: prop, header: 'Users' });
                } else if (prop.substring(0, 8) === 'calendar') {
                    headers.push({ field: prop, header: 'Calendar' });
                } else if (prop === 'groupByValue') {
                    headers.push({ field: prop, header: 'Value' });
                } else if (prop === 'groupByCompanyPool') {
                    headers.push({ field: prop, header: 'Companies' });
                } else if (prop.substring(0, 6) === 'answer') {
                    headers.push({ field: prop, header: seriesLabel });
                }
            }
        }
    }

    // deep copy to make them independent
    let rows = JSON.parse(JSON.stringify(parseGroupTokens(resultData, labels)))

    return {
        rows: rows,
        headers: headers
    }
}

function parseGroupTokens(data, labels) {

    data.forEach((item) => {
        for (const prop in item) {
            if (prop === 'groupBySex' ) {
                if (Object.hasOwn(labels, 'groupBySex')) {
                    const value = item['groupBySex'];
                    if (value === null) {
                        item['groupBySex'] = 'N.A.';
                    } else if (Object.hasOwn(labels['groupBySex'], value)) {
                        item['groupBySex'] = labels['groupBySex'][value];
                    }
                }
            } else if (prop === 'groupByValue' ) {
                if (Object.hasOwn(labels, 'groupByValue')) {
                    const value = item['groupByValue'];
                    if (value === null) {
                        item['groupByValue'] = 'N.A.';
                    } else if (Object.hasOwn(labels['groupByValue'], value)) {
                        item['groupByValue'] = labels['groupByValue'][value];
                    }
                }
            } else if (prop === 'groupByCompanyPool' ) {
                if (Object.hasOwn(labels, 'groupByCompanyPool')) {
                    const value = item['groupByCompanyPool'];
                    if (value === null) {
                        item['groupByCompanyPool'] = 'N.A.';
                    } else if (Object.hasOwn(labels['groupByCompanyPool'], value)) {
                        item['groupByCompanyPool'] = labels['groupByCompanyPool'][value];
                    }
                }
            } else if (prop === 'age_today_range') {
                if (Object.hasOwn(labels, 'age_today_range')) {
                    const value = item['age_today_range'];
                    if (value === null) {
                        item['age_today_range'] = 'N.A.';
                    } else if (Object.hasOwn(labels['age_today_range'], value)) {
                        item['age_today_range'] = labels['age_today_range'][value];
                    }
                }
            } else if (prop === 'age_assessment_range') {
                if (Object.hasOwn(labels, 'age_assessment_range')) {
                    const value = item['age_assessment_range'];
                    if (value === null) {
                        item['age_assessment_range'] = 'N.A.';
                    } else if (Object.hasOwn(labels['age_assessment_range'], value)) {
                        item['age_assessment_range'] = labels['age_assessment_range'][value];
                    }
                }
            } else if (prop === 'groupByPassFail') {
                if (Object.hasOwn(labels, 'groupByPassFail')) {
                    const value = item['groupByPassFail'];
                    if (value === null) {
                        item['groupByPassFail'] = 'N.A.';
                    } else if (Object.hasOwn(labels['groupByPassFail'], value)) {
                        item['groupByPassFail'] = labels['groupByPassFail'][value];
                    }
                }
            } else if (prop === 'groupByUsers') {
                if (Object.hasOwn(labels, 'groupByUsers')) {
                    const value = item['groupByUsers'];
                    if (value === null) {
                        item['groupByUsers'] = 'N.A.';
                    } else if (Object.hasOwn(labels['groupByUsers'], value)) {
                        item['groupByUsers'] = labels['groupByUsers'][value];
                    }
                }
            } else if (prop.substring(0, 10) === 'calendar__' ) {
                //Original string: calendar___238
                //the first part is constant, the second will be the group id

                const separator = '___';
                const procedureFragments = prop.split(separator);
                const value = item[prop];

                sortedGroups.value.forEach((group) => {
                    if (group.id!==null  && group.id.toString() === procedureFragments[1].toString()) {

                        if (typeof value === 'number' && group.first === 'days') {

                            // The integer part of the integer division will return the year,
                            // the remainder the day's number (1 to 366). 0 mean the last day of a leap year.
                            //
                            // E.g. 740886
                            // Days: 2024-04-11 , 2024-04-10 , 2024-04-09
                            //
                            // Day's number is the last day of the interval.

                            const year = Math.trunc(value / 366);
                            const day = value % 366;

                            item[prop] = value;

                            if (value > 0 && year > 0) {
                                item[prop] = DateTime.fromObject({
                                        year: year,
                                        ordinal: day - (group.second - 1)
                                    }).toFormat('dd-MM-yyyy') + ' to '
                                    + DateTime.fromObject({
                                        year: year,
                                        ordinal: day
                                    }).toFormat('dd-MM-yyyy') ;
                            }

                        } if (typeof value === 'number' && group.first === 'years') {
                            if ( group.second && group.second>1) {
                                // the given number is the last year of the period
                                const start = value - (group.second - 1);

                                item[prop] = start + ' to ' + value;
                            } else {
                                item[prop] = value
                            }
                        } else if (typeof value === 'number' && group.first === 'months') {
                            const year = Math.floor((value - 1) / 12);
                            const month = ((value - 1) % 12) + 1;

                            const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul","Aug", "Sep", "Oct", "Nov", "Dec"];

                            if ( group.second && group.second>1) {
                                // the given number is the last year of the period
                                const start = month - (group.second - 1);

                                item[prop] = monthNames[start-1] + ' to ' + monthNames[month-1] + ' ('+year+')';
                            } else {
                                item[prop] = monthNames[month-1] + ' ('+year+')'
                            }
                        } else if (typeof value === 'number' && group.first === 'weeks') {
                            //const year = Math.trunc(value / 12);
                            //const month = value % 12;


                            const year = value.toString().substring(0, 4)
                            const week = parseInt(value.toString().substring(4, 6) ); // *1 to convert it into a number

                            if ( group.second && group.second>1) {
                                // the given number is the last year of the period
                                const start = week - (group.second - 1);

                                item[prop] = 'From week ' + start + ' to ' + week + ' ('+year+')'

                                //item[prop] = monthNames[start-1] + ' to ' + monthNames[month-1] + ' ('+year+')';
                            } else {
                                item[prop] = 'Week ' + week + ' ('+year+')'
                            }


                        } else {
                            //console.log('unrecognised group', group.first);
                        }
                    }
                })

            } else if (prop !== 'answer') {
                // console.log('unhandled prop', prop)
            }
        }
    })

    return data
}

function parsingMetricResponse(response) {

    metric.value.id = response.data.metric.id
    metric.value.name = response.data.metric.name
    metric.value.code = response.data.metric.code
    metric.value.company_id = response.data.metric.company_id
    metric.value.description = response.data.metric.description
    metric.value.sharable = response.data.metric.sharable
    metric.value.type = response.data.metric.type
    metric.value.groups = response.data.metric.groups
    metric.value.series = response.data.metric.series

    console.log('parsingMetricResponse', response.data.metric.series)

    graphDatasets.value = {
        labels: [],
        dataset: [],
        seriesLabel: '',
    };

    tableData.value = {
        headers: [],
        rows: [],
    };

    graphDatasets.value.dataset = new Map();

    metric.value.groups.forEach((group) => {

        if (group.procedure === 'values') {
            const config = JSON.parse(group.first)

            if (config !== null && (Object.hasOwn(config, 'tag_id') || Object.hasOwn(config, 'attribute_code'))) {
                parseValueGroupConfig(config)
            }
        }
    })

    metric.value.series.forEach((series) => {
        //const seriesId = series.id;
        const seriesLabel = series.label;

        if (Object.hasOwn(response.data.series.data, seriesLabel)) {
            series.result = response.data.series.data[seriesLabel].details.result;
            series.output = response.data.series.data[seriesLabel].details.output;

            series.tableData = buildTableData(response.data.series.data[seriesLabel].details.result, response.data.series.labels, seriesLabel)

            series.labels = response.data.series.labels;

            const dataset = buildDatasets(response.data.series.data[seriesLabel].details.result, sortedGroups.value, response.data.series.labels, series.label);

            const labels = buildLabels(response.data.series.data[seriesLabel].details.output, sortedGroups.value, response.data.series.labels, series.label)

            series.dataset = {
                labels: labels,
                dataset: dataset.dataset,
                seriesLabel: dataset.labels
            }

            graphDatasets.value.labels = labels

            graphDatasets.value.seriesLabel = dataset.labels

            dataset.dataset.forEach((item) => {
                graphDatasets.value.dataset.set(seriesLabel, item)
            })

            // Each series should be grouped with the same logic so the columns should match.

            series.tableData.headers.forEach(item => {

                // deep copy to make them independent
                let data = JSON.parse(JSON.stringify(item))

                if (data.field.substring(0, 6) === 'answer') {
                    data.field = 'answer_'+series.label.toLowerCase().replaceAll(/ /g, '_')
                }

                let found = false

                tableData.value.headers.forEach(tableHeader => {
                    if (tableHeader.field === data.field) {
                        found = true;
                    }
                })

                if (!found) {
                    tableData.value.headers.push(data)
                }

            })

            series.tableData.rows.forEach((item, index) => {
                // deep copy to make them independent
                let data = JSON.parse(JSON.stringify(item))

                for (const prop in data) {
                    let field = prop;

                    if (field === 'answer') {
                        field = 'answer_'+series.label.toLowerCase().replaceAll(/ /g, '_')
                        data[field] = data.answer
                        delete data.answer
                    }

                    if (typeof tableData.value.rows[index] === 'undefined') {
                        tableData.value.rows[index] = {}
                    }

                    tableData.value.rows[index][field] = data[field]
                }
            })


        }
    })

    tableData.value.headers.sort((a, b) => a.field.localeCompare(b.field));

    //console.log('overall tableData', tableData.value)
}

function buildDatasets(seriesOutput, groupDefinition, seriesLabels, metricName) {
    let dataset = new Map()
    let labels = new Map()

    // Looking for the inner group. If it doesn't exist, use the only one that exists.
    const groupIndex = groupDefinition.length - 1;

    if (typeof groupDefinition[groupIndex] != 'undefined') {
        // At least a group is defined

        const groupProcedure = groupDefinition[groupIndex].procedure;

        seriesOutput.forEach((item) => {
            let key = '';

            if (groupProcedure === 'sex') {
                key = 'groupBySex';
            } else if (groupProcedure === 'age_today_range') {
                key = 'age_today_range';
            } else if (groupProcedure === 'age_assessment_range') {
                key = 'age_assessment_range';
            } else if (groupProcedure === 'pass_fail') {
                key = 'groupByPassFail';
            } else if (groupProcedure === 'users') {
                key = 'groupByUsers';
            } else if (groupProcedure === 'companies') {
                key = 'groupByCompanyPool';
            } else if (groupProcedure === 'values') {
                key = 'groupByValue';
            } else if (groupProcedure === 'calendar') {
                /*
                Output: calendar___300 to match a record like answer:
                "77.875000", groupBySex: "Female", calendar___300: "Aug (2024)"
                 */
                key = groupProcedure + '___' + groupDefinition[groupIndex].id //id
            }

            let groupKey = null

            if (groupIndex === 0) {
                // only one group level
                groupKey = 0
                labels.set(groupKey, metricName)
            } else {
                groupKey = item[key]

                if (Object.hasOwn(seriesLabels, key)) {
                    if (typeof seriesLabels[key][groupKey] === 'undefined') {
                        labels.set(groupKey, item[key]);
                    } else {
                        labels.set(groupKey, seriesLabels[key][groupKey]);
                    }
                }
            }

            let seriesData = dataset.get(groupKey)
            if (typeof seriesData === 'undefined') {
                seriesData = [];
            }

            seriesData.push(item.answer)
            dataset.set(groupKey, seriesData);

        })

        return {
            dataset: dataset, labels: labels
        };
    } else {
        //console.log('seriesOutput', seriesOutput);

        let groupKey = 0
        labels.set(groupKey, metricName)

        let seriesData = [];

        seriesOutput.forEach((item) => {
            seriesData.push(item.answer)
            dataset.set(groupKey, seriesData);
        })

        return {
            dataset: dataset, labels: labels
        }
    }
}

function buildLabels(seriesOutput, groupDefinition, staticLabels, metricName) {
    let labels = [];

    if (groupDefinition.length > 0) {
        const firstGroup = groupDefinition[0]

        if (firstGroup.procedure === 'sex') {
            if (Object.hasOwn(staticLabels, 'groupBySex')) {

                //  Adding N.A.
                labels.push('N.A.')

                for (const key in staticLabels.groupBySex) {
                    const label = staticLabels.groupBySex[key]
                    labels.push(label)
                }
            }

        } else if (firstGroup.procedure === 'age_today_range') {
            if (Object.hasOwn(staticLabels, 'age_today_range')) {
                if (Object.hasOwn(seriesOutput, 'age_today_range')) {
                    for (const key in seriesOutput.age_today_range) {
                        const label = staticLabels.age_today_range[key]
                        labels.push(label)
                    }
                }
            }
        } else if (firstGroup.procedure === 'values') {


            if (Object.hasOwn(staticLabels, 'groupByValue')) {
                if (Object.hasOwn(seriesOutput, 'groupByValue')) {
                    for (const key in seriesOutput.groupByValue) {
                        let label = staticLabels.groupByValue[key]

                        if (typeof label === 'undefined') {
                            label = 'N.A.'
                        }

                        labels.push(label)

                    }
                }
            }
        } else if (firstGroup.procedure === 'pass_fail') {
            if (Object.hasOwn(staticLabels, 'groupByPassFail')) {
                if (Object.hasOwn(seriesOutput, 'groupByPassFail')) {
                    for (const key in seriesOutput.groupByPassFail) {
                        const label = staticLabels.groupByPassFail[key]
                        labels.push(label)
                    }
                }
            }
        } else if (firstGroup.procedure === 'users') {
            if (Object.hasOwn(staticLabels, 'groupByUsers')) {
                if (Object.hasOwn(seriesOutput, 'groupByUsers')) {
                    for (const key in seriesOutput.groupByUsers) {
                        const label = staticLabels.groupByUsers[key]
                        labels.push(label)
                    }
                }
            }
        } else if (firstGroup.procedure === 'age_assessment_range') {
            if (Object.hasOwn(staticLabels, 'age_assessment_range')) {
                if (Object.hasOwn(seriesOutput, 'age_assessment_range')) {
                    for (const key in seriesOutput.age_assessment_range) {
                        const label = staticLabels.age_assessment_range[key]
                        labels.push(label)
                    }
                }
            }
        } else if (firstGroup.procedure === 'companies') {
            if (Object.hasOwn(staticLabels, 'groupByCompanyPool')) {
                // console.log('seriesOutput', seriesOutput);
                if (Object.hasOwn(seriesOutput, 'groupByCompanyPool')) {
                    for (const key in seriesOutput.groupByCompanyPool) {
                        const label = staticLabels.groupByCompanyPool[key]
                        labels.push(label)
                    }
                }
            }
        } else if (firstGroup.procedure === 'calendar') {
            /*
            The calendar group will have the following signature:
            calendar___<id>
            E.g., calendar___46
             */

            const groupKey = 'calendar___'+firstGroup.id
            if (Object.hasOwn(seriesOutput, groupKey)) {

                const calendarGroup = firstGroup.first
                const calendarQty = firstGroup.second

                for (const value in seriesOutput[groupKey]) {
                    if (calendarGroup === 'weeks') {
                        const year = value.toString().substring(0, 4)
                        const week = parseInt(value.toString().substring(4, 6) ); // *1 to convert it into a number

                        if ( firstGroup.second && firstGroup.second>1) {
                            // the given number is the last year of the period
                            const start = week - (firstGroup.second - 1);

                            labels.push('From week ' + start + ' to ' + week + ' ('+year+')')

                            //item[prop] = monthNames[start-1] + ' to ' + monthNames[month-1] + ' ('+year+')';
                        } else {
                            labels.push('Week ' + week + ' ('+year+')')
                        }
                    } else if (calendarGroup === 'months') {
                        const year = Math.floor((value - 1) / 12);
                        const month = ((value - 1) % 12) + 1;

                        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul","Aug", "Sep", "Oct", "Nov", "Dec"];

                        if ( firstGroup.second && firstGroup.second>1) {
                            // the given number is the last year of the period
                            const start = month - (firstGroup.second - 1);

                            labels.push(monthNames[start-1] + ' to ' + monthNames[month-1] + ' ('+year+')');
                        } else {
                            labels.push(monthNames[month-1] + ' ('+year+')')
                        }
                    } else if (calendarGroup === 'years') {
                        if ( firstGroup.second && firstGroup.second>1) {
                            // the given number is the last year of the period
                            const start = value - (firstGroup.second - 1);

                            labels.push(start + ' to ' + value);
                        } else {
                            labels.push(value)
                        }
                    } else if (calendarGroup === 'days') {
                        const qty = parseInt(calendarQty)

                        if (!isNaN(qty) && qty > 0) {
                            const year = Math.trunc(value / 366);
                            const day = value % 366;

                            if (value > 0 && year > 0) {
                                labels.push(
                                    DateTime.fromObject({
                                        year: year,
                                        ordinal: day
                                    }).toFormat('dd-MM-yyyy') + ' to '
                                    + DateTime.fromObject({
                                        year: year,
                                        ordinal: day - (calendarQty - 1)
                                    }).toFormat('dd-MM-yyyy')
                                );
                            }
                        }
                    }
                }
            }
        } else {
            //console.log('firstGroup.procedure', firstGroup.procedure)
        }
    } else {
        // console.log('[buildLabels] seriesOutput', seriesOutput)
        // console.log('[buildLabels] groupDefinition', groupDefinition)
        // console.log('[buildLabels] staticLabels', staticLabels)
        // console.log('[buildLabels] metricName', metricName)

        labels.push(metricName)
    }


    return labels;
}

function onPeriodChange() {
    if (props.id !== undefined) {
        loadMetricDetail(props.id);
    }
}

function handleMetricLoadFailure(error) {
    console.log('error', error);
    loading.value = false;

    // getRequest resolves errors (does not throw); interceptor may pass error.response
    const status = error?.statusCode ?? error?.status ?? error?.response?.status
    const isTimeout = status === 504
        || error?.code === 'ECONNABORTED'
        || /timeout/i.test(String(error?.message ?? error?.statusText ?? ''))

    if (isTimeout) {
        showToast(
            { severity: 'warn', summary: 'Timeout' },
            'Metric load timed out. Try a shorter period (e.g. Last 3 months), or All time may be too large for this company.'
        )
        return
    }

    showToast({}, error?.data?.message ?? error?.message ?? 'Failed to load metric')
    router.go(-1)
}

function loadMetricDetail(metricId) {
    loading.value = true;

    const params = {
        company_id: company_id.value,
    }
    // 0 / null = all time (no months query param) — same contract as dashboard MetricGraph
    if (selectedMonths.value != null && selectedMonths.value > 0) {
        params.months = selectedMonths.value
    }

    GlobalRequest.getRequest('/v2/datasource/metric/'+metricId, {
        params,
    }).then((response) => {
        // GlobalRequest.getRequest returns err objects instead of rejecting
        if (!response || response.statusCode !== 200) {
            handleMetricLoadFailure(response)
            return
        }

        if (response.data) {
            metricSeriesGraphData.value = response.data
        }

        parsingMetricResponse(response)
        loading.value = false;
    }).catch((error) => {
        handleMetricLoadFailure(error)
    });
}


</script>


<style lang="scss" scoped>
.form-field {
    display: flex;
    flex-direction: column;
    margin-bottom: 0.5rem;
}

.form-field label {
    margin-bottom: 0.5rem;
}

.metric-period-dropdown {
    min-width: 10rem;
}
</style>